import React, { useState } from 'react';
import axios from 'axios';

const ChefProfileForm = ({ user, profile, onUpdate }) => {
  const [formData, setFormData] = useState({
    bio: profile?.bio || '',
    specialties: profile?.specialties?.join(', ') || '',
    cuisine_tags: profile?.cuisine_tags?.join(', ') || '',
    service_cities: profile?.service_cities?.join(', ') || '',
    max_guests: profile?.max_guests || 20
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg('');

    try {
      await axios.post('/chef/profile', {
        user_id: user.id,
        bio: formData.bio,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(s => s),
        cuisine_tags: formData.cuisine_tags.split(',').map(c => c.trim()).filter(c => c),
        service_cities: formData.service_cities.split(',').map(c => c.trim()).filter(c => c),
        max_guests: parseInt(formData.max_guests)
      });
      
      setMsg('✅ Profile saved successfully!');
      onUpdate();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) return;
    
    const fd = new FormData();
    fd.append('file', photoFile);
    fd.append('user_id', user.id);

    try {
      const response = await axios.post('/chef/photo', fd);
      setMsg('✅ Photo uploaded! (mocked)');
      setPhotoFile(null);
      onUpdate();
    } catch (err) {
      setMsg('Photo upload failed');
    }
  };

  const handleVideoUpload = async () => {
    if (!videoFile) return;
    
    const fd = new FormData();
    fd.append('file', videoFile);
    fd.append('user_id', user.id);

    try {
      const response = await axios.post('/chef/video', fd);
      setMsg('✅ Video uploaded! (mocked)');
      setVideoFile(null);
      onUpdate();
    } catch (err) {
      setMsg('Video upload failed');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="chef-profile-form">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
          <textarea
            rows="4"
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            placeholder="Tell customers about yourself..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            data-testid="bio-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Specialties (comma-separated)
          </label>
          <input
            type="text"
            value={formData.specialties}
            onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
            placeholder="e.g., Pasta, Grilling, Desserts"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            data-testid="specialties-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cuisine Tags (comma-separated)
          </label>
          <input
            type="text"
            value={formData.cuisine_tags}
            onChange={(e) => setFormData({ ...formData, cuisine_tags: e.target.value })}
            placeholder="e.g., Italian, French, Japanese"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            data-testid="cuisine-tags-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Service Cities (comma-separated)
          </label>
          <input
            type="text"
            value={formData.service_cities}
            onChange={(e) => setFormData({ ...formData, service_cities: e.target.value })}
            placeholder="e.g., Atlanta, New York, Los Angeles"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            data-testid="service-cities-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max Guests</label>
          <input
            type="number"
            min="1"
            max="100"
            value={formData.max_guests}
            onChange={(e) => setFormData({ ...formData, max_guests: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            data-testid="max-guests-input"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          data-testid="save-profile-button"
        >
          {loading ? 'Saving...' : 'Save Profile'}
        </button>

        {msg && (
          <div className={`text-sm ${
            msg.includes('✅') ? 'text-green-600' : 'text-red-600'
          }`} data-testid="profile-message">
            {msg}
          </div>
        )}
      </form>

      {/* SafeServ Certificate */}
      <div className="mt-8 pt-8 border-t">
        <h3 className="text-lg font-semibold mb-2">SafeServ Certification</h3>
        {profile?.safeserv_cert_number && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>Cert #:</strong> {profile.safeserv_cert_number}
            </p>
            {profile.safeserv_verified ? (
              <p className="text-sm text-green-600 mt-1">✅ Verified by admin</p>
            ) : (
              <p className="text-sm text-yellow-600 mt-1">⏳ Pending verification</p>
            )}
          </div>
        )}
        
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setPhotoFile(e.target.files[0])}
            className="text-sm"
            data-testid="cert-input"
          />
          <button
            onClick={async () => {
              if (!photoFile) return;
              const fd = new FormData();
              fd.append('file', photoFile);
              fd.append('user_id', user.id);
              try {
                await axios.post('/chef/safeserv-cert', fd);
                setMsg('✅ Certificate uploaded! Pending admin verification.');
                setPhotoFile(null);
                onUpdate();
              } catch (err) {
                setMsg('Certificate upload failed');
              }
            }}
            disabled={!photoFile}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
            data-testid="upload-cert-button"
          >
            Upload Certificate
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Upload your SafeServ certification document (PDF, JPG, or PNG)
        </p>
      </div>

      {/* Photo Upload */}
      <div className="mt-8 pt-8 border-t">
        <h3 className="text-lg font-semibold mb-4">Upload Photos & Videos</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPhotoFile(e.target.files[0])}
              className="text-sm"
              data-testid="photo-input"
            />
            <button
              onClick={handlePhotoUpload}
              disabled={!photoFile}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              data-testid="upload-photo-button"
            >
              Upload Photo
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setVideoFile(e.target.files[0])}
              className="text-sm"
              data-testid="video-input"
            />
            <button
              onClick={handleVideoUpload}
              disabled={!videoFile}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
              data-testid="upload-video-button"
            >
              Upload Video
            </button>
          </div>
          
          <p className="text-xs text-gray-500">
            <strong>Note:</strong> File uploads use mocked AWS S3 storage
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChefProfileForm;
